/**
 * Phase 7 §2.8 — Prebuilt “quick” reports: shared date range, CSV per section.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Download, RefreshCw, TrendingUp, Users, CheckCircle, Award, Zap } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell, PieChart, Pie, Tooltip } from 'recharts';

const CHART_COLORS = ['#10B981', '#6EE7B7', '#06B6D4', '#0EA5E9', '#8B5CF6', '#EC4899', '#F59E0B', '#EF4444'];
import { supabase } from '@core/supabase/client';
import { computeCompositeScore } from '@core/ai/interviewAssessment';
import type { AssessmentScores } from '@core/ai/interviewAssessment';
import { hasMeaningfulAssessment } from '@modules/ai-interviews/aiInterviewReviewerUi';
import { formatDateOrDateTimeGmt } from '@shared/lib/formatDateTimeGmt';
import { useToast } from '@shared/hooks/useToast';

function defaultDateRange(): { from: string; to: string } {
  const to = new Date();
  const from = new Date(to);
  from.setDate(from.getDate() - 6);
  return { from: from.toISOString().slice(0, 10), to: to.toISOString().slice(0, 10) };
}

function dayStartIso(ymd: string): string {
  return new Date(`${ymd}T00:00:00`).toISOString();
}

function dayEndIso(ymd: string): string {
  return new Date(`${ymd}T23:59:59.999`).toISOString();
}

function downloadCsv(filename: string, rows: Record<string, string | number | null | undefined>[]) {
  if (!rows.length) return;
  const keys = Object.keys(rows[0]);
  const esc = (v: unknown) => {
    const s = v == null ? '' : String(v);
    return `"${s.replace(/"/g, '""')}"`;
  };
  const lines = [keys.join(','), ...rows.map((r) => keys.map((k) => esc(r[k])).join(','))];
  const blob = new Blob([`\uFEFF${lines.join('\r\n')}`], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function normalizeScores(raw: unknown): AssessmentScores | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const out: AssessmentScores = {};
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
    const n = Number(v);
    if (Number.isFinite(n)) out[k] = n;
  }
  return Object.keys(out).length > 0 ? out : null;
}

function TableSkeleton({ rows = 4 }: { rows?: number }) {
  return (
    <div className="space-y-2 animate-pulse" aria-hidden>
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="h-8 rounded bg-[var(--vscode-bg-tertiary)] opacity-60" />
      ))}
    </div>
  );
}

function ReportCard({
  title,
  description,
  onCsv,
  csvDisabled,
  children,
  loading,
}: {
  title: string;
  description: string;
  onCsv: () => void;
  csvDisabled?: boolean;
  children: React.ReactNode;
  loading: boolean;
}) {
  return (
    <div className="card p-4 rounded-xl ring-1 ring-[color-mix(in_srgb,var(--vscode-border-primary)_35%,transparent)] space-y-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h3 className="text-sm font-semibold vscode-text-primary">{title}</h3>
          <p className="text-[10px] vscode-text-tertiary mt-0.5 max-w-xl">{description}</p>
        </div>
        <button
          type="button"
          className="btn-secondary text-xs inline-flex items-center gap-1 shrink-0"
          onClick={onCsv}
          disabled={csvDisabled || loading}
          title="Download CSV"
        >
          <Download className="w-3.5 h-3.5" />
          CSV
        </button>
      </div>
      {loading ? <TableSkeleton /> : children}
    </div>
  );
}

export type ReportsCannedSectionProps = {
  orgId: string | null | undefined;
  canExportCsv: boolean;
};

export function ReportsCannedSection({ orgId, canExportCsv }: ReportsCannedSectionProps) {
  const initial = useMemo(() => defaultDateRange(), []);
  const [dateFrom, setDateFrom] = useState(initial.from);
  const [dateTo, setDateTo] = useState(initial.to);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { toast, showToast } = useToast();

  const [topMatches, setTopMatches] = useState<
    {
      matched_at: string;
      composite_score: number;
      candidate_name: string;
      job_title: string;
      recommendation: string | null;
    }[]
  >([]);

  const [interviewRows, setInterviewRows] = useState<
    {
      id: string;
      candidate_name: string;
      completed_at: string;
      status: string;
      composite: number | null;
      pass: boolean;
      completed_too_short: boolean;
    }[]
  >([]);

  const [stageRows, setStageRows] = useState<
    { from_status: string; to_status: string; transitions: number }[]
  >([]);

  const [aiSpendDetail, setAiSpendDetail] = useState<
    { feature: string; model: string | null; calls: number; total_tokens: number; total_credits: number }[]
  >([]);

  const rangeIso = useMemo(
    () => ({ start: dayStartIso(dateFrom), end: dayEndIso(dateTo) }),
    [dateFrom, dateTo]
  );

  const load = useCallback(async () => {
    if (!orgId) return;
    setLoading(true);
    setError(null);
    const { start, end } = rangeIso;

    try {
      const [mr, iv, st, ai] = await Promise.all([
        supabase
          .from('ggj_b2b_match_results')
          .select(
            `
            matched_at,
            composite_score,
            recommendation,
            candidate:ggj_b2b_candidates(full_name),
            job:ggj_b2b_jobs(title)
          `
          )
          .eq('org_id', orgId)
          .gte('matched_at', start)
          .lte('matched_at', end)
          .order('composite_score', { ascending: false })
          .limit(100),

        supabase
          .from('ggj_b2b_ai_interview_sessions')
          .select(
            'id, status, completed_at, assessed_at, assessment_scores, completed_too_short, candidate:ggj_b2b_candidates(full_name)'
          )
          .eq('org_id', orgId)
          .not('completed_at', 'is', null)
          .gte('completed_at', start)
          .lte('completed_at', end)
          .order('completed_at', { ascending: false })
          .limit(500),

        supabase
          .from('ggj_b2b_application_status_history')
          .select('from_status, to_status, changed_at')
          .gte('changed_at', start)
          .lte('changed_at', end)
          .limit(5000),

        supabase
          .from('ggj_b2b_ai_usage_log')
          .select('feature, model, prompt_tokens, completion_tokens, credits_used')
          .eq('org_id', orgId)
          .eq('status', 'ok')
          .gte('created_at', start)
          .lte('created_at', end)
          .limit(8000),
      ]);

      if (mr.error) throw new Error(mr.error.message);
      if (iv.error) throw new Error(iv.error.message);
      if (st.error) throw new Error(st.error.message);
      if (ai.error) throw new Error(ai.error.message);

      const matches = (mr.data ?? []).map((row: Record<string, unknown>) => {
        const cand = row.candidate as { full_name?: string | null } | null;
        const job = row.job as { title?: string | null } | null;
        return {
          matched_at: String(row.matched_at ?? ''),
          composite_score: Number(row.composite_score ?? 0),
          candidate_name: cand?.full_name?.trim() || '—',
          job_title: job?.title?.trim() || '—',
          recommendation: typeof row.recommendation === 'string' ? row.recommendation : null,
        };
      });
      setTopMatches(matches);

      const ivParsed = (iv.data ?? []).map((row: Record<string, unknown>) => {
        const scores = normalizeScores(row.assessment_scores);
        const assessedAt = (row.assessed_at as string | null) ?? null;
        const meaningful = hasMeaningfulAssessment(assessedAt, scores);
        const composite = meaningful && scores ? computeCompositeScore(scores) : null;
        const tooShort = Boolean(row.completed_too_short);
        const st = String(row.status ?? '').toLowerCase();
        const pass =
          st !== 'cancelled' &&
          !tooShort &&
          meaningful &&
          composite != null &&
          composite >= 60;
        const cand = row.candidate as { full_name?: string | null } | null;
        return {
          id: String(row.id),
          candidate_name: cand?.full_name?.trim() || '—',
          completed_at: String(row.completed_at ?? ''),
          status: String(row.status ?? ''),
          composite,
          pass,
          completed_too_short: tooShort,
        };
      });
      setInterviewRows(ivParsed);

      const stageMap = new Map<string, number>();
      for (const row of st.data ?? []) {
        const r = row as { from_status?: string | null; to_status?: string | null };
        const from = r.from_status == null || r.from_status === '' ? '(initial)' : String(r.from_status);
        const to = r.to_status == null || r.to_status === '' ? '—' : String(r.to_status);
        const key = `${from}\t${to}`;
        stageMap.set(key, (stageMap.get(key) ?? 0) + 1);
      }
      const stageList = [...stageMap.entries()]
        .map(([k, transitions]) => {
          const [from_status, to_status] = k.split('\t');
          return { from_status, to_status, transitions };
        })
        .sort((a, b) => b.transitions - a.transitions);
      setStageRows(stageList);

      const spendMap = new Map<string, { feature: string; model: string | null; calls: number; tokens: number; credits: number }>();
      for (const row of ai.data ?? []) {
        const r = row as {
          feature?: string;
          model?: string | null;
          prompt_tokens?: number | null;
          completion_tokens?: number | null;
          credits_used?: number | null;
        };
        const feature = String(r.feature ?? 'unknown');
        const model = r.model ?? null;
        const key = `${feature}\t${model ?? ''}`;
        const cur = spendMap.get(key) ?? { feature, model, calls: 0, tokens: 0, credits: 0 };
        cur.calls += 1;
        cur.tokens += (r.prompt_tokens ?? 0) + (r.completion_tokens ?? 0);
        cur.credits += Number(r.credits_used ?? 0);
        spendMap.set(key, cur);
      }
      const spendList = [...spendMap.values()].sort((a, b) => b.credits - a.credits || b.calls - a.calls);
      setAiSpendDetail(
        spendList.map((s) => ({
          feature: s.feature,
          model: s.model,
          calls: s.calls,
          total_tokens: s.tokens,
          total_credits: Math.round(s.credits * 10000) / 10000,
        }))
      );
    } catch (e) {
      console.error('[ReportsCannedSection]', e);
      setError(e instanceof Error ? e.message : 'Could not load quick reports.');
    } finally {
      setLoading(false);
    }
  }, [orgId, rangeIso]);

  useEffect(() => {
    void load();
  }, [load]);

  const interviewDenom = interviewRows.filter((r) => r.status.toLowerCase() !== 'cancelled').length;
  const interviewPass = interviewRows.filter((r) => r.pass).length;
  const passRatePct = interviewDenom > 0 ? Math.round((interviewPass / interviewDenom) * 1000) / 10 : null;

  const totalAiCredits = aiSpendDetail.reduce((s, r) => s + r.total_credits, 0);

  return (
    <div className="space-y-4">
      {toast}
      <div className="flex flex-wrap items-center gap-3 p-3 vscode-bg-secondary border vscode-border rounded-xl">
        <span className="text-xs font-medium vscode-text-secondary">Date range (all quick reports)</span>
        <input
          type="date"
          value={dateFrom}
          onChange={(e) => setDateFrom(e.target.value)}
          className="input text-xs py-1.5 w-36"
        />
        <span className="vscode-text-tertiary text-xs">to</span>
        <input
          type="date"
          value={dateTo}
          onChange={(e) => setDateTo(e.target.value)}
          className="input text-xs py-1.5 w-36"
        />
        <button
          type="button"
          className="btn-secondary text-xs inline-flex items-center gap-1"
          onClick={() => void load()}
          disabled={loading || !orgId}
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {error ? (
        <div className="rounded-lg border border-red-800/50 bg-red-950/30 px-4 py-3 text-sm text-red-300">{error}</div>
      ) : null}

      <ReportCard
        title="Top matches"
        description="Smart Match rows in this range, highest composite score first (up to 100)."
        loading={loading}
        csvDisabled={!canExportCsv}
        onCsv={() => {
          if (!canExportCsv) return;
          if (!topMatches.length) {
            showToast({ title: 'No top matches in this date range.', kind: 'error' });
            return;
          }
          downloadCsv(
            `quick_report_top_matches_${dateFrom}_${dateTo}.csv`,
            topMatches.map((r) => ({
              matched_at: formatDateOrDateTimeGmt(r.matched_at),
              composite_score: r.composite_score,
              candidate: r.candidate_name,
              job: r.job_title,
              recommendation: r.recommendation ?? '',
            }))
          );
        }}
      >
        {topMatches.length === 0 ? (
          <p className="text-xs vscode-text-tertiary py-4 text-center">No matches in this range.</p>
        ) : (
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Summary stats */}
            <div className="flex gap-3 lg:flex-col lg:w-32 flex-shrink-0">
              <div className="rounded-xl vscode-bg-secondary px-4 py-3 flex-1 lg:flex-none">
                <div className="flex items-center gap-2 mb-1">
                  <Award className="h-4 w-4 text-emerald-400" />
                  <p className="text-[10px] vscode-text-tertiary uppercase">Total</p>
                </div>
                <p className="text-2xl font-bold tabular-nums">{topMatches.length}</p>
              </div>
              <div className="rounded-xl vscode-bg-secondary px-4 py-3 flex-1 lg:flex-none">
                <div className="flex items-center gap-2 mb-1">
                  <TrendingUp className="h-4 w-4 text-blue-400" />
                  <p className="text-[10px] vscode-text-tertiary uppercase">Avg score</p>
                </div>
                <p className="text-2xl font-bold tabular-nums">
                  {Math.round(topMatches.reduce((s, r) => s + r.composite_score, 0) / topMatches.length)}
                </p>
              </div>
            </div>
            {/* Table with inline score bars */}
            <div className="flex-1 overflow-x-auto -mx-1">
              <table className="w-full text-xs ggj-data-table">
                <thead>
                  <tr>
                    <th className="text-left">Candidate</th>
                    <th className="text-left">Job</th>
                    <th className="text-left w-32">Score</th>
                    <th className="text-left">Matched</th>
                  </tr>
                </thead>
                <tbody>
                  {topMatches.slice(0, 12).map((r, i) => (
                    <tr key={`${r.matched_at}-${i}-${r.candidate_name}`}>
                      <td className="font-medium">{r.candidate_name}</td>
                      <td className="vscode-text-secondary">{r.job_title}</td>
                      <td>
                        <div className="flex items-center gap-2">
                          <div className="flex-1 h-2 rounded-full bg-gray-700/50 overflow-hidden max-w-[60px]">
                            <div
                              className="h-full rounded-full transition-all"
                              style={{
                                width: `${r.composite_score}%`,
                                backgroundColor: r.composite_score >= 70 ? '#10B981' : r.composite_score >= 50 ? '#F59E0B' : '#6B7280',
                              }}
                            />
                          </div>
                          <span className="tabular-nums font-semibold min-w-[24px] text-right">{r.composite_score}</span>
                        </div>
                      </td>
                      <td className="vscode-text-tertiary">{formatDateOrDateTimeGmt(r.matched_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {topMatches.length > 12 ? (
                <p className="text-[10px] vscode-text-tertiary mt-2">Showing 12 of {topMatches.length}. CSV includes all.</p>
              ) : null}
            </div>
          </div>
        )}
      </ReportCard>

      <ReportCard
        title="AI interview pass rate"
        description="Completed AI interviews in range. Pass = rubric composite ≥ 60, not too short, and not cancelled."
        loading={loading}
        csvDisabled={!canExportCsv}
        onCsv={() => {
          if (!canExportCsv) return;
          if (!interviewRows.length) {
            showToast({ title: 'No completed AI interviews in this date range.', kind: 'error' });
            return;
          }
          downloadCsv(
            `quick_report_ai_interviews_${dateFrom}_${dateTo}.csv`,
            interviewRows.map((r) => ({
              session_id: r.id,
              candidate: r.candidate_name,
              completed_at: formatDateOrDateTimeGmt(r.completed_at),
              status: r.status,
              composite: r.composite ?? '',
              pass: r.pass ? 'yes' : 'no',
              completed_too_short: r.completed_too_short ? 'yes' : 'no',
            }))
          );
        }}
      >
        <div className="flex flex-col sm:flex-row gap-4 mb-3">
          {/* Visual gauge */}
          <div className="flex-shrink-0 flex flex-col items-center justify-center">
            <div className="relative w-28 h-28">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={[
                      { name: 'Passed', value: interviewPass || 0 },
                      { name: 'Failed', value: Math.max(0, interviewDenom - interviewPass) },
                    ]}
                    cx="50%"
                    cy="50%"
                    innerRadius={30}
                    outerRadius={45}
                    dataKey="value"
                    startAngle={90}
                    endAngle={-270}
                    stroke="none"
                  >
                    <Cell fill="#10B981" />
                    <Cell fill="#374151" />
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-xl font-bold">{passRatePct == null ? '—' : `${passRatePct}%`}</span>
                <span className="text-[9px] vscode-text-tertiary">pass rate</span>
              </div>
            </div>
          </div>
          {/* Stats grid */}
          <div className="grid grid-cols-2 gap-2 flex-1">
            <div className="rounded-xl vscode-bg-secondary px-4 py-3 flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-500/20">
                <Users className="h-4 w-4 text-blue-400" />
              </div>
              <div>
                <p className="text-[10px] vscode-text-tertiary uppercase">Completed</p>
                <p className="text-lg font-semibold tabular-nums">{interviewDenom}</p>
              </div>
            </div>
            <div className="rounded-xl vscode-bg-secondary px-4 py-3 flex items-center gap-3">
              <div className="p-2 rounded-lg bg-emerald-500/20">
                <CheckCircle className="h-4 w-4 text-emerald-400" />
              </div>
              <div>
                <p className="text-[10px] vscode-text-tertiary uppercase">Passed</p>
                <p className="text-lg font-semibold tabular-nums text-emerald-400">{interviewPass}</p>
              </div>
            </div>
          </div>
        </div>
        {interviewRows.length === 0 ? (
          <p className="text-xs vscode-text-tertiary py-2 text-center">No completed AI interviews in this range.</p>
        ) : (
          <div className="overflow-x-auto -mx-1 max-h-56 overflow-y-auto">
            <table className="w-full text-xs ggj-data-table">
              <thead>
                <tr>
                  <th className="text-left">Candidate</th>
                  <th className="text-left">Completed</th>
                  <th className="text-right">Composite</th>
                  <th className="text-left">Pass</th>
                </tr>
              </thead>
              <tbody>
                {interviewRows.slice(0, 20).map((r) => (
                  <tr key={r.id}>
                    <td>{r.candidate_name}</td>
                    <td className="vscode-text-tertiary">{formatDateOrDateTimeGmt(r.completed_at)}</td>
                    <td className="text-right tabular-nums">{r.composite ?? '—'}</td>
                    <td>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium ${r.pass ? 'bg-emerald-500/20 text-emerald-400' : 'bg-gray-500/20 text-gray-400'}`}>
                        {r.pass ? 'Pass' : 'Fail'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </ReportCard>

      <ReportCard
        title="Stage conversion"
        description="Counts of application status transitions in this range (from_status → to_status)."
        loading={loading}
        csvDisabled={!canExportCsv}
        onCsv={() => {
          if (!canExportCsv) return;
          if (!stageRows.length) {
            showToast({ title: 'No stage transitions in this date range.', kind: 'error' });
            return;
          }
          downloadCsv(
            `quick_report_stage_conversion_${dateFrom}_${dateTo}.csv`,
            stageRows.map((r) => ({
              from_status: r.from_status,
              to_status: r.to_status,
              transitions: r.transitions,
            }))
          );
        }}
      >
        {stageRows.length === 0 ? (
          <p className="text-xs vscode-text-tertiary py-4 text-center">No status transitions in this range.</p>
        ) : (
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Bar chart for top transitions */}
            <div className="flex-1 min-w-0">
              <p className="text-[10px] vscode-text-tertiary uppercase mb-2">Top transitions</p>
              <div className="h-40">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={stageRows.slice(0, 8).map((r) => ({
                      name: `${r.to_status}`,
                      value: r.transitions,
                    }))}
                    layout="vertical"
                    margin={{ top: 0, right: 20, bottom: 0, left: 60 }}
                  >
                    <XAxis type="number" tick={{ fontSize: 10, fill: 'var(--vscode-text-tertiary)' }} axisLine={false} tickLine={false} />
                    <YAxis type="category" dataKey="name" tick={{ fontSize: 10, fill: 'var(--vscode-text-secondary)' }} axisLine={false} tickLine={false} width={55} />
                    <Tooltip
                      contentStyle={{ backgroundColor: 'var(--vscode-bg-secondary)', border: '1px solid var(--vscode-border-primary)', borderRadius: '8px', fontSize: '11px' }}
                      labelStyle={{ color: 'var(--vscode-text-primary)' }}
                    />
                    <Bar dataKey="value" radius={[0, 4, 4, 0]} maxBarSize={20}>
                      {stageRows.slice(0, 8).map((_, idx) => (
                        <Cell key={idx} fill={CHART_COLORS[idx % CHART_COLORS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
            {/* Table for details */}
            <div className="flex-1 overflow-x-auto max-h-48 overflow-y-auto">
              <table className="w-full text-xs ggj-data-table">
                <thead>
                  <tr>
                    <th className="text-left">From</th>
                    <th className="text-left">To</th>
                    <th className="text-right">Count</th>
                  </tr>
                </thead>
                <tbody>
                  {stageRows.slice(0, 15).map((r, i) => (
                    <tr key={`${i}-${r.from_status}-${r.to_status}`}>
                      <td className="vscode-text-secondary">{r.from_status}</td>
                      <td>{r.to_status}</td>
                      <td className="text-right tabular-nums font-medium">{r.transitions}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {stageRows.length > 15 ? (
                <p className="text-[10px] vscode-text-tertiary mt-2">Showing 15 of {stageRows.length}. CSV includes all.</p>
              ) : null}
            </div>
          </div>
        )}
      </ReportCard>

      <ReportCard
        title="AI spend"
        description="Sum of successful AI usage log rows in this range, rolled up by feature and model."
        loading={loading}
        csvDisabled={!canExportCsv}
        onCsv={() => {
          if (!canExportCsv) return;
          if (!aiSpendDetail.length) {
            showToast({ title: 'No AI usage in this date range.', kind: 'error' });
            return;
          }
          downloadCsv(
            `quick_report_ai_spend_${dateFrom}_${dateTo}.csv`,
            aiSpendDetail.map((r) => ({
              feature: r.feature,
              model: r.model ?? '',
              calls: r.calls,
              total_tokens: r.total_tokens,
              total_credits: r.total_credits,
            }))
          );
        }}
      >
        <div className="flex flex-col sm:flex-row gap-4 mb-3">
          {/* Total credits card */}
          <div className="rounded-xl vscode-bg-secondary px-4 py-3 flex items-center gap-3 min-w-[140px]">
            <div className="p-2 rounded-lg bg-amber-500/20">
              <Zap className="h-5 w-5 text-amber-400" />
            </div>
            <div>
              <p className="text-[10px] vscode-text-tertiary uppercase">Credits used</p>
              <p className="text-xl font-bold tabular-nums">
                {totalAiCredits > 0 ? totalAiCredits.toFixed(2) : '0'}
              </p>
            </div>
          </div>
          {/* Pie chart for distribution */}
          {aiSpendDetail.length > 0 && (
            <div className="flex-1 flex items-center gap-4">
              <div className="w-24 h-24 flex-shrink-0">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={aiSpendDetail.slice(0, 6).map((r) => ({
                        name: r.feature.replace(/_/g, ' '),
                        value: r.total_credits,
                      }))}
                      cx="50%"
                      cy="50%"
                      innerRadius={20}
                      outerRadius={40}
                      dataKey="value"
                      stroke="none"
                    >
                      {aiSpendDetail.slice(0, 6).map((_, idx) => (
                        <Cell key={idx} fill={CHART_COLORS[idx % CHART_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{ backgroundColor: 'var(--vscode-bg-secondary)', border: '1px solid var(--vscode-border-primary)', borderRadius: '8px', fontSize: '10px' }}
                      formatter={(value: number) => value.toFixed(4)}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="flex flex-wrap gap-x-3 gap-y-1 text-[10px]">
                {aiSpendDetail.slice(0, 6).map((r, idx) => (
                  <div key={r.feature} className="flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full" style={{ backgroundColor: CHART_COLORS[idx % CHART_COLORS.length] }} />
                    <span className="vscode-text-secondary truncate max-w-[80px]" title={r.feature}>{r.feature.replace(/_/g, ' ')}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        {aiSpendDetail.length === 0 ? (
          <p className="text-xs vscode-text-tertiary py-2 text-center">No AI usage in this range.</p>
        ) : (
          <div className="overflow-x-auto -mx-1">
            <table className="w-full text-xs ggj-data-table">
              <thead>
                <tr>
                  <th className="text-left">Feature</th>
                  <th className="text-left">Model</th>
                  <th className="text-right">Calls</th>
                  <th className="text-right">Tokens</th>
                  <th className="text-right">Credits</th>
                </tr>
              </thead>
              <tbody>
                {aiSpendDetail.map((r, idx) => (
                  <tr key={`${r.feature}-${r.model ?? ''}`}>
                    <td className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: CHART_COLORS[idx % CHART_COLORS.length] }} />
                      {r.feature}
                    </td>
                    <td className="vscode-text-secondary">{r.model ?? '—'}</td>
                    <td className="text-right tabular-nums">{r.calls}</td>
                    <td className="text-right tabular-nums">{r.total_tokens.toLocaleString()}</td>
                    <td className="text-right tabular-nums font-medium">{r.total_credits.toFixed(4)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </ReportCard>
    </div>
  );
}
